import './Other.sass';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useRoute, useRouter } from 'react-router5';
import { useDispatch, useSelector } from 'react-redux';

import { Header, List, ListItem, ScrollView } from 'uikit';
import {
  ADS,
  BATTLES,
  CLOUD,
  DASHBOARD,
  FREE_WRITE,
  GENDER_FILTER,
  GROUPS,
  INCOGNITO,
  INVITE,
  LISTS,
  MINE,
  PAYMENT_METHODS,
  PERSONAL_SUBSCRIPTION_INFO,
  PROFILE,
  RESOLVE,
  SETTINGS,
  STATISTICS,
  STATISTICS_PRO,
  STORAGE,
  SUBSCRIPTION_MEDIA,
  SUBSCRIPTIONS,
  VERIFICATION_APPLY,
  VERIFICATION_LANDING,
} from 'shared/constants/pages';
import { useCurrentUserInfo } from 'hooks/useUserInfo';
import { AuthRequired } from '$core/AuthRequired/AuthRequired';
import { authActions, authSelector } from '$store/auth';
import {
  IcAds28,
  IcBankCard28,
  IcChart28,
  IcChevron16,
  IcCloud28,
  IcContest28,
  IcDiamond,
  IcDollar28,
  IcEdit28,
  IcIncognito28,
  IcList28,
  IcMessage28,
  IcMonetization28,
  IcNewTab28,
  IcPickaxe28,
  IcSettings28,
  IcSupport28,
  IcUserPlus28,
  IcUserQuestionmark28,
  IcUsers28,
  IcUserSponsor28,
} from '$assets';
import { MetaTags, UnitName, UnitPhoto } from '$shared/components';
import { useTranslator, useWikiLink } from '$hooks';
import { contentPrioritySelector, mainSelector, managerLinkSelector } from '$store/main';
import { formatCurrency } from '$utils';
import { ContentPriorityType } from '$store/main/interface';
import { UserSwitch } from './UserSwitch/UserSwitch';
import { CurrencyEnum, GenderFilterEnum } from '$store/models';
import { PersonalManagerBottomSheet } from '$core/PersonalManagerBottomSheet/PersonalManagerBottomSheet';

export function Other() {
  const {
    route: { meta },
  } = useRoute();
  const dispatch = useDispatch();
  const user = useCurrentUserInfo();
  const { isLogged } = useSelector(authSelector);
  const t = useTranslator();
  const supportLink = useWikiLink('/general-information/customer-support');
  const { balance, mVerified, eurBalance, genderFilter, isCryptoEnabled } =
    useSelector(mainSelector);
  const contentPriority = useSelector(contentPrioritySelector);
  const isAdultPriority = contentPriority === ContentPriorityType.ADULT;
  const router = useRouter();
  const managerLink = useSelector(managerLinkSelector);
  const [showManagerBottomSheet, setShowManagerBottomSheet] = useState(false);

  const genderFilterLabel = useMemo(() => {
    if (
      genderFilter === GenderFilterEnum.WOMEN ||
      genderFilter === GenderFilterEnum.NONE
    ) {
      return t('gender_filter_option_women');
    } else if (genderFilter === GenderFilterEnum.MEN) {
      return t('gender_filter_option_men');
    } else if (genderFilter === GenderFilterEnum.BOTH) {
      return t('gender_filter_option_both');
    }
  }, [genderFilter]);

  const handleSubscription = useCallback(() => {
    router.navigate(SUBSCRIPTION_MEDIA);
  }, []);

  useEffect(() => {
    dispatch(authActions.reloadAccounts());
  }, []);

  if (!isLogged) {
    return <AuthRequired />;
  }

  return (
    <>
      {showManagerBottomSheet && (
        <PersonalManagerBottomSheet onClose={() => setShowManagerBottomSheet(false)} />
      )}
      <MetaTags title={t('other_title')} />
      <Header showBackButton={meta?.options?.previousRoute === DASHBOARD}>
        {t('other_title')}
      </Header>
      <ScrollView>
        <Link
          routeName={user.domain ? RESOLVE : PROFILE}
          routeParams={user.domain ? { domain: user.domain } : { id: user!.id }}
          className="Other__profile"
        >
          <UnitPhoto unit={user} onlineIndicator={false} />
          <div className="Other__profile__cont">
            <div className="Other__profile__name">
              <UnitName unit={user} />
            </div>
            <div className="Other__profile__caption">
              {t('other_go_to_profile')}
              <IcChevron16 />
            </div>
          </div>
        </Link>
        {/*{!decryptKeys[-2] && contentPriority === ContentPriorityType.OTHER && (*/}
        {/*  <div className="Other__subscription">*/}
        {/*    <Banner*/}
        {/*      title={t('balance_subscription_banner_title')}*/}
        {/*      caption={t('balance_subscription_banner_caption')}*/}
        {/*      onClick={handleSubscription}*/}
        {/*    />*/}
        {/*  </div>*/}
        {/*)}*/}

        <div className="Other__list">
          {!window.isWebApp && <UserSwitch />}

          {isCryptoEnabled && user.verified !== 2 && (
            <List>
              <ListItem icon={<IcPickaxe28 />} routeName={MINE} color="red">
                Mining
              </ListItem>
            </List>
          )}

          <List>
            {isAdultPriority && !user?.isolated && (
              <ListItem routeName={GENDER_FILTER} indicator={genderFilterLabel}>
                {t('gender_filter_title')}
              </ListItem>
            )}
            <ListItem
              indicator={
                user.verified === 2 || isAdultPriority
                  ? formatCurrency(eurBalance, CurrencyEnum.EUR)
                  : formatCurrency(balance, CurrencyEnum.TON)
              }
              modal="balance"
            >
              {t('other_balance')}
            </ListItem>
            {user.verified !== 2 && (
              <ListItem icon={<IcDiamond />} routeName={SUBSCRIPTIONS}>
                {t('other_subscriptions')}
              </ListItem>
            )}
            {user.verified !== 2 && (
              <ListItem icon={<IcDollar28 />} routeName={STORAGE}>
                {t('purchased_content')}
              </ListItem>
            )}
            <ListItem icon={<IcBankCard28 />} routeName={PAYMENT_METHODS}>
              {t('payment_methods_content')}
            </ListItem>
            <ListItem icon={<IcEdit28 />} modal="edit">
              {t('other_edit')}
            </ListItem>
            {mVerified && (
              <ListItem icon={<IcCloud28 />} routeName={CLOUD}>
                {t('cloud_storage')}
              </ListItem>
            )}
            {mVerified && (
              <ListItem icon={<IcList28 />} routeName={LISTS}>
                {t('lists_title')}
              </ListItem>
            )}
            {!!managerLink && (
              <ListItem
                onClick={() => setShowManagerBottomSheet(true)}
                target="_blank"
                icon={<IcUserQuestionmark28 />}
                // description={t('other_manager_caption')}
              >
                {t('other_manager')}
              </ListItem>
            )}
            {user.verified === 2 && (
              <ListItem icon={<IcIncognito28 />} routeName={INCOGNITO}>
                {t('incognito')}
              </ListItem>
            )}
            {user.verified === 2 && (
              <ListItem routeName={FREE_WRITE} icon={<IcMessage28 />}>
                {t('free_write_title')}
              </ListItem>
            )}
            {user.verified === 2 && (
              <ListItem
                href="https://t.me/+soU0TZBo0aw1YmEy"
                target="_blank"
                icon={<IcNewTab28 />}
              >
                {t('other_models_channel')}
              </ListItem>
            )}
          </List>
          <List>
            <ListItem icon={<IcUserPlus28 />} routeName={INVITE}>
              {t('other_invite')}
            </ListItem>
            {isAdultPriority && !user?.isolated && (
              <ListItem icon={<IcContest28 />} routeName={BATTLES}>
                {t('other_battles')}
              </ListItem>
            )}
            {(!isAdultPriority || mVerified) && (
              <ListItem routeName={PERSONAL_SUBSCRIPTION_INFO} icon={<IcUserSponsor28 />}>
                {t('other_sponsors')}
              </ListItem>
            )}
            {isAdultPriority && (
              <ListItem
                icon={<IcMonetization28 style={{ color: 'var(--tint)' }} />}
                routeName={VERIFICATION_LANDING}
              >
                {t('other_monetization')}
              </ListItem>
            )}
            {!isAdultPriority && (
              <ListItem
                icon={<IcMonetization28 style={{ color: 'var(--tint)' }} />}
                routeName={VERIFICATION_APPLY}
              >
                {t('other_monetization')}
              </ListItem>
            )}
            {!isAdultPriority && (
              <>
                <ListItem icon={<IcUsers28 />} routeName={GROUPS}>
                  {t('other_groups')}
                </ListItem>
              </>
            )}
            {mVerified && (
              <ListItem icon={<IcChart28 />} routeName={STATISTICS}>
                {t('other_statistics')}
              </ListItem>
            )}
            {mVerified && (
              <ListItem
                icon={
                  <span className="Other__newBadge">{t('statistics_pro_badge_new')}</span>
                }
                routeName={STATISTICS_PRO}
              >
                {t('statistics_pro_title')}
              </ListItem>
            )}
            <ListItem icon={<IcSupport28 />} href={supportLink} target="_blank">
              {t('other_support')}
            </ListItem>
            {(!isAdultPriority || mVerified) && !user?.isolated && (
              <ListItem icon={<IcAds28 />} routeName={ADS}>
                {t('other_ads')}
              </ListItem>
            )}
            <ListItem icon={<IcSettings28 />} routeName={SETTINGS}>
              {t('other_settings')}
            </ListItem>
          </List>
        </div>
      </ScrollView>
    </>
  );
}
